import { useCallback, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { Form, Switch } from 'antd';

import { InfoCardList } from '@src/components/InfoCard';
import { DASHBOARD_ITEM_API_NAMES, SCHEDULE_TYPES } from '@src/constants/commons';
import TableService from '@src/services/TableService';

import {
  ALL_TEMPLATES,
  DASHBOARDS_EVENT_TYPE,
  LAYOUT_BY_COMPONENT_TYPE,
  TEMPLATE_IDS_DEFAULT,
} from '../../../constants';
import { DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE } from '../constants';

const useDeviationsFromTaskCompliance = ({ chartId, divId, boardId }) => {
  const tableService = useMemo(() => new TableService(), []);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions);
  const orgId = useSelector(({ organizations }) => organizations?.organization?.id);
  const [form] = Form.useForm();

  /* Handlers */
  const parseColumnsTable = useCallback(columns => {
    if (!columns) return [];
    return columns.map(item => ({
      isActive: item.isActive,
      key: item.key,
      order: item.order,
    }));
  }, []);

  const parseColumnsTableFetched = useCallback(columns => {
    const fetchedMap = new Map(
      (columns || []).map(item => [item.key, { isActive: item.isActive, order: item.order }]),
    );

    return DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE.map((sourceCol, index) => {
      const fetched = fetchedMap.get(sourceCol.key);
      return {
        key: sourceCol.key,
        name: sourceCol.name,
        description: sourceCol.description,
        isActive: fetched?.isActive ?? sourceCol.isActive,
        order: fetched?.order ?? sourceCol.order ?? index + 1,
      };
    }).sort((a, b) => a.order - b.order);
  }, []);

  const handleClear = useCallback(() => {
    form.resetFields();
    form.setFieldValue('dataSourceTable', DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE);
    form.setFieldValue('templateIds', TEMPLATE_IDS_DEFAULT);
  }, [form]);

  const buildBody = useCallback(
    value => {
      const {
        title,
        description,
        templateIds,
        divisionIds,
        dataSourceTable: dataSourceTableValue,
      } = value;
      const layout = form.getFieldValue('layout');

      const body = {
        title: title,
        description: description || '',
        layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.TABLES],
        apiName: DASHBOARD_ITEM_API_NAMES.EXECUTION_RECORDS_DASHBOARD,
        queryParams: {
          templateIds: templateIds?.includes(ALL_TEMPLATES) ? [] : templateIds || [],
          divisionIds: divisionIds || [],
          type: SCHEDULE_TYPES.task,
          columns: parseColumnsTable(dataSourceTableValue),
        },
      };
      form.setFieldValue('formData', body);
      return body;
    },
    [form, parseColumnsTable],
  );

  const handleClearTemplateIds = useCallback(() => {
    form.setFieldValue('templateIds', TEMPLATE_IDS_DEFAULT);

    const allValues = form.getFieldsValue();
    buildBody({ ...allValues, templateIds: TEMPLATE_IDS_DEFAULT });
  }, [buildBody, form]);

  const divisions = useMemo(
    () =>
      Object.values(userOrgDivisions || {})
        .map(division => ({
          name: division.name,
          value: division.id,
        }))
        .filter(division => (divId ? division.value === divId : true)),
    [userOrgDivisions, divId],
  );

  /* Columns for the table */
  const columns = useMemo(
    () => DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE.sort((a, b) => a.order - b.order),
    [],
  );

  const onSortableMove = useCallback(
    cols => {
      const newColumns = cols.map((column, i) => ({ ...column, index: i, order: i + 1 }));

      const allValues = form.getFieldsValue();
      buildBody({
        ...allValues,
        dataSourceTable: newColumns,
      });
    },
    [buildBody, form],
  );

  const onManageColumns = useCallback(
    col => {
      const currentColumns = form.getFieldValue('dataSourceTable');

      const newColumns = currentColumns.map(c => {
        if (c.key === col.key) {
          return { ...c, isActive: !c.isActive };
        }
        return c;
      });

      form.setFieldValue('dataSourceTable', newColumns);
      const allValues = form.getFieldsValue();
      buildBody({
        ...allValues,
        dataSourceTable: newColumns,
      });
    },
    [buildBody, form],
  );

  const checkingIfColumnIsAllowed = useCallback(
    componentId => {
      const element = columns.find(c => c.key === componentId);
      return !element?.isActive;
    },
    [columns],
  );

  const ColumnItem = useMemo(
    () =>
      // eslint-disable-next-line react/prop-types
      ({ data = {} }) => (
        <InfoCardList
          extraRightContent={
            <div>
              <Switch
                defaultChecked={data?.isActive}
                checked={data?.isActive}
                onChange={() => onManageColumns(data)}
                disabled={checkingIfColumnIsAllowed(data?.key)}
              />
            </div>
          }
          title={data?.name}
          subTitle={data?.description}
        />
      ),
    [checkingIfColumnIsAllowed, onManageColumns],
  );

  /* Load fetch data from the form */
  useEffect(() => {
    if (!chartId || !boardId || !orgId) return;

    const fetchData = async () => {
      const res = await tableService.getTable({ orgId, divId, boardId, tableId: chartId });

      if (!res) return;

      const { title, description, layout, queryParams } = res;

      form.setFieldsValue({
        id: chartId,
        title,
        description,
        layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.TABLES],
        templateIds: queryParams.templateIds?.length
          ? queryParams.templateIds
          : TEMPLATE_IDS_DEFAULT,
        divisionIds: queryParams.divisionIds || [],
        dataSourceTable: parseColumnsTableFetched(queryParams.columns || []),
        formData: res,
      });
    };
    fetchData();
  }, [boardId, divId, form, orgId, chartId, tableService, parseColumnsTableFetched]);

  return {
    divisions,
    orgId,
    form,
    buildBody,
    handleClear,
    handleClearTemplateIds,
    ColumnItem,
    onSortableMove,
  };
};

export default useDeviationsFromTaskCompliance;
